/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React, {
  forwardRef,
  MutableRefObject,
  useEffect,
  useReducer,
  useRef,
} from "react";

import cn from "classnames";

import { Nullable } from "constants/interfaces";
import isUndefined from "utils/isUndefined";

import { LoadingDots } from "../Loaders";
import TimePickerIcon from "components/icons/TimePickerIcon";

import s from "./Input.module.css";
import InputDate from "./InputDate";
import radioStyle from "./RadioInput.module.css";
import { InputInterface } from "./interface";

const Input = forwardRef(
  (
    {
      type,
      isAssignCheck = false,
      className,
      isMissing = false,
      isLoading,
      size = "lg",
      partialSelected,
      isTodayHidden,
      testId,
      ...rest
    }: InputInterface,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === "function") {
        ref(node);
      } else if (ref && "current" in ref) {
        (ref as MutableRefObject<HTMLInputElement | null>).current = node;
      }
    };

    if (type === "radio") {
      return (
        <input
          ref={ref}
          type="radio"
          {...rest}
          className={cn(radioStyle.native, className)}
          test-id="O1H5o6FK"
        />
      );
    } else if (type === "checkbox") {
      return (
        <div className={cn(s.checkbox, s[size])} test-id="03ZPPS2G">
          <input
            ref={ref}
            type="checkbox"
            {...rest}
            className={cn(className, {
              "cursor-pointer": !rest.disabled,
            })}
            test-id="TH4jmpBs"
          />

          <span
            className={cn(
              isAssignCheck ? s.assignCheckmark : s.checkmark,
              s[size],
              {
                "border-[1px] w-[20px] h-[20px]": isAssignCheck,
                "cursor-pointer": !rest.disabled,
              },
            )}
            test-id="I8eoH_QX"
          >
            {partialSelected ? (
              <span
                className="h-full w-full flex items-center justify-center"
                test-id="2oK_Yj1O"
              >
                <svg
                  width="10"
                  height="2"
                  viewBox="0 0 10 2"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  test-id="NVUpVEha"
                >
                  <path
                    d="M0.916504 1H9.08317"
                    stroke="#D6D6D6"
                    stroke-width="1.66666"
                    stroke-linejoin="round"
                    test-id="wROQPc18"
                  />
                </svg>
              </span>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill={isAssignCheck ? "#fff" : "currentColor"}
                className="w-0 h-0 duration-150"
                viewBox="0 0 16 16"
                test-id="N4enx68U"
              >
                <path
                  d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"
                  test-id="J4i4Zueh"
                />
              </svg>
            )}
          </span>
        </div>
      );
    } else if (type === "date") {
      return (
        <InputDate
          ref={ref}
          isLoading={isLoading}
          isMissing={isMissing}
          {...rest}
          isTodayHidden={isTodayHidden}
          test-id="UvrMPFND"
        />
      );
    } else {
      return (
        <div className="relative w-full" test-id="M5jU6vkx">
          <input
            ref={handleRef}
            type={type}
            maxLength={rest.maxLength ?? 500}
            {...rest}
            className={cn(s.root, className, {
              "border-alert": isMissing,
            })}
            test-id={testId}
          />

          <div
            className={cn(
              "absolute h-fit right-5 top-1/2 transform -translate-y-1/2 -translate-x-1/2",
              !isLoading && "hidden",
            )}
            test-id="mb1K0Yme"
          >
            <LoadingDots test-id="Q09kky6n" />
          </div>

          {type === "time" && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
              onClick={() => {
                const el = inputRef.current;
                if (!el) return;
                try {
                  if (typeof el.showPicker === "function") {
                    el.showPicker();
                  } else {
                    el.focus();
                    el.click();
                  }
                } catch {
                  el.focus();
                  el.click();
                }
              }}
              aria-label="Open time picker"
            >
              <TimePickerIcon fill="#fff" />
            </button>
          )}
        </div>
      );
    }
  },
);

export default Input;

// This input is used to show a preview value, but only do process the onChange action
// (which could be a fetch request) when the user clicks outside "onBlur"
export const DeferredInput = ({ ...args }) => {
  const inputRef = useRef<Nullable<HTMLInputElement>>(null);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (inputRef.current) {
      // if it has been already assigned, then omit.
      const isRefAssignedInitialValue = !!inputRef.current.value;

      if (isRefAssignedInitialValue) return;
      // I am using `isUndefined`, because the value could be null or falsy value like empty string...etc
      if (isUndefined(args.value) === false) {
        inputRef.current.value = args.value;
      }
    }
  }, [args.value]);

  return (
    <Input
      {...args}
      ref={inputRef}
      value={inputRef.current?.value}
      // The preview value, not the real value.
      onChange={(e) => {
        if (inputRef.current) {
          inputRef.current.value = e.target.value;
          forceUpdate();
        }
      }}
      // passing the preview value to be set as the "real value"
      onBlur={(e) => {
        if (args?.onChange) {
          args.onChange(e);
        }

        if (args?.onBlur) {
          args.onBlur(e);
        }
      }}
      test-id="MSIJ9KOu"
    />
  );
};
